import React, { useMemo, useRef, useEffect } from "react";

import { TemplateQuestionFragment } from "../../../graphql/fragments";
import { ReportAnswer } from "../../../graphql/types";

import useTranslations from "../../../hooks/useTranslations";

import {
  Badge,
  Checkbox,
  CheckboxGroup,
  Input,
  Textarea,
} from "../../wombat-ui";

import {
  getReportAnswersByRole,
  TemplateQuestionImplProps,
  TemplateQuestionPreviewImplProps,
} from "../TemplateQuestion";
import TemplateQuestionTitle from "./utils/TemplateQuestionTitle";
import Answer from "./utils/Answer";

type TemplateQuestionSelectorMultipleOptions = {
  value: string;
  label: string;
};

type TemplateQuestionSelectorMultipleValue = {
  selector: string[];
  comment: string;
  timeSpent: number;
};

type TemplateQuestionSelectorMultiple = {
  options: TemplateQuestionSelectorMultipleOptions[];
  defaultValue: TemplateQuestionSelectorMultipleValue;
} & TemplateQuestionFragment;

export function isTemplateQuestionSelectorMultiple(
  q: TemplateQuestionFragment
): q is TemplateQuestionSelectorMultiple {
  return q.type === "TemplateQuestionSelectorMultiple";
}

export function parseQuestion(
  question: TemplateQuestionFragment
): TemplateQuestionSelectorMultiple {
  return question as TemplateQuestionSelectorMultiple;
}

type ReportAnswerSelectorMultiple = {
  value: TemplateQuestionSelectorMultipleValue;
} & ReportAnswer;

export function parseAnswers(
  answers: ReportAnswer[]
): ReportAnswerSelectorMultiple[] {
  return answers as ReportAnswerSelectorMultiple[];
}

type TemplateQuestionSelectorMultipleProps = TemplateQuestionImplProps;

export const TemplateQuestionSelectorMultiple: React.FC<TemplateQuestionSelectorMultipleProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    mad,
    register,
    status,
    displayIndex,
    role,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getReportAnswersByRole(rawAnswers, parseAnswers, mad),
      [rawAnswers]
    );

    const firstCheckboxRef = useRef(null);
    useEffect(() => {
      if (autoFocus) firstCheckboxRef.current?.focus();
    }, [firstCheckboxRef.current, autoFocus]);

    return (
      <div>
        <CheckboxGroup
          display="row"
          title={
            <TemplateQuestionTitle
              displayIndex={displayIndex}
              title={question.label}
              description={question.description}
              referents={question.referents}
            />
          }
          description={t("reports.multipleSelectorQuestion") as string}
        >
          {question.options.map((opt, index) => (
            <Checkbox
              key={opt.value}
              name={`${name}.value.selector`}
              label={opt.label}
              id={`${name}.value.selector_${opt.value}`}
              value={opt.value}
              disabled={!needsToBeFilled}
              ref={(ref) => {
                if (index === 0) firstCheckboxRef.current = ref;
                register(ref);
              }}
              variant="boxed"
              defaultChecked={answers?.[
                role === "talent" ? "talent" : "referent"
              ]?.[0]?.value?.selector.includes(opt.value)}
            />
          ))}
        </CheckboxGroup>
        {question.timeTracked && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            min={0}
            id={`${question.id}-timeTracked`}
            name={`${name}.timeSpent`}
            disabled={!needsToBeFilled}
            ref={register({ valueAsNumber: true })}
            defaultValue={answers?.talent?.[0]?.timeSpent || undefined}
          />
        )}
        {needsToBeFilled && (
          <div className="max-w-2xl pt-4">
            <Textarea
              label={t("reports.comments") as string}
              id={question.id}
              rows={2}
              ref={register()}
              name={`${name}.value.comment`}
              defaultValue={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
                  .comment
              }
              help={t("reports.commentsHelp") as string}
            />
          </div>
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.talentAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.talent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={a.value.selector.map((s) => (
                    <Badge key={s}>
                      {question.options.find((o) => o.value === s)?.label}
                    </Badge>
                  ))}
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
        {status !== "to_be_filled_evaluator" && answers.referent.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referent.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={a.value.selector.map((s) => (
                    <Badge key={s}>
                      {question.options.find((o) => o.value === s)?.label}
                    </Badge>
                  ))}
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type TemplateQuestionSelectorMultiplePreviewProps = TemplateQuestionPreviewImplProps;

export const TemplateQuestionSelectorMultiplePreview: React.FC<TemplateQuestionSelectorMultiplePreviewProps> = React.memo(
  ({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <CheckboxGroup
          display="row"
          title={
            <TemplateQuestionTitle
              displayIndex={displayIndex}
              title={question.label}
            />
          }
          description={t("reports.multipleSelectorQuestion") as string}
        >
          {question.options.map((opt) => (
            <Checkbox
              key={`${opt.value}`}
              label={opt.label}
              id={`${question.label}_${opt.value}`}
              value={opt.value}
              disabled
              variant="boxed"
            />
          ))}
        </CheckboxGroup>
      </div>
    );
  }
);
